<template>
  <div v-if='rec'>
    <wc-page-title title='Reconciliation Detail'>
      <template v-slot:rightSide>
        <v-icon v-if='rec' :color="rec.is_locked ? 'red' : 'grey'">mdi-lock</v-icon>
        <wc-burger-menu>
          <v-list-item @click='toggleLock' :disabled='rec.archived'>
            <v-list-item-icon>
              <v-icon color='red'>mdi-lock</v-icon>
            </v-list-item-icon>
            <v-list-item-title v-if='rec.is_locked'>UN-Lock Reconciliation</v-list-item-title>
            <v-list-item-title v-else>Lock Reconciliation</v-list-item-title>
          </v-list-item>
        </wc-burger-menu>
      </template>
    </wc-page-title>
    <v-container>
      <v-row>
        <v-col cols='12' md='4'>
          <wc-data-box
            :title='toolbarData.dataBox.basics.title'
            :titleIcon='toolbarData.dataBox.basics.icon'
            :titleIconColor='toolbarData.dataBox.basics.iconColor'
            :showEdit='!rec.is_locked'
            @edit='editRec'
          >
            <wc-two-column-table :items='basicsData'></wc-two-column-table>
          </wc-data-box>
        </v-col>
        <v-col cols='12' offset-md='4' md='4'>
          <wc-data-box
            title='Status'
          >
            <wc-two-column-table :items='statusData'></wc-two-column-table>
            <v-card-text class='text-center'>
              <span :class="diff !== 0 ? 'display-2 error--text' : 'display-2 success--text'">{{ diff | decAccounting }}</span><br>
              <small>(should be zero)</small>
            </v-card-text>
          </wc-data-box>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols='12'>
          <wc-data-box title='Transactions to Include' :loading='isLoading'>
            <v-simple-table dense loading>
              <thead>
                <tr class='overline'>
                  <th>Date</th>
                  <th width='125' class='text-right'>Expenses</th>
                  <th width='125' class='text-right'>Deposits</th>
                  <th width='60'></th>
                  <th width='60'></th>
                </tr>
              </thead>

              <tbody v-if='rec'>
                <rec-line
                  v-for='(l, index) in lines'
                  :key='index'
                  :line='l'
                  :rec='rec'
                  @updateLine='updateLine'
                ></rec-line>
              </tbody>
            </v-simple-table>
          </wc-data-box>
        </v-col>
      </v-row>
    </v-container>
    <reconciliation-tracker
      v-if='rec'
      :reconciliation='rec'
      ref='actionTracker'
    ></reconciliation-tracker>

    <wc-dialog
      :showDialog='showDialog'
      :title='toolbarData.dataBox.basics.title'
      :icon='toolbarData.dataBox.basics.icon'
      :disableButtons='!isFormValid'
      :loading='isSaving'
      width='400'
      @cancel='showDialog = false'
      @save='updateRec'
    >
      <v-form v-model='isFormValid'>
        <wc-datepicker
          v-model='recCopy.from_date'
          label='Date from'
          required
        ></wc-datepicker>
        <wc-datepicker
          v-model='recCopy.to_date'
          label='Date to'
          required
        ></wc-datepicker>
        <v-text-field
          prepend-icon='mdi-currency-usd'
          label='Ending balance'
          :rules='[rules.decimal]'
          v-model='recCopy.end_balance'
        ></v-text-field>
      </v-form>
    </wc-dialog>
  </div>
  <div v-else>
    <wc-loading line></wc-loading>
  </div>
</template>

<script>
import trackerAPI from '@/api/tracker';
import accountingAPI from '@/api/accounting';
import ReconciliationTracker from '@/components/trackers/ReconciliationTracker';
import moment from 'moment';
import _ from 'lodash';
import StatementLine from './rows/StatementLine';
import miscServices from '@/services/misc';
import formRules from '@/mixins/formRules';

export default {
  name: 'reconciliation-detail',
  mixins: [formRules],
  data: () => ({
    isLoading: true,
    isSaving: false,
    showDialog: false,
    isFormValid: false,
    rec: null,
    recCopy: {},
    deposits: [],
    expenses: []
  }),
  computed: {
    toolbarData () {
      return this.$store.state.icons
    },
    lines () {
      let deps = []
      this.deposits.forEach(d => {
        deps.push({
          date: d.date,
          reconciliation: d.reconciliation,
          is_deposit: true,
          is_expense: false,
          deposit: d,
          expense: null,
          deposit_amount: d.amount,
          expense_amount: null,
        })
      })
      // expenses
      this.expenses.forEach(d => {
        deps.push({
          date: d.expense_date,
          reconciliation: d.reconciliation,
          is_deposit: false,
          is_expense: true,
          deposit: null,
          expense: d,
          deposit_amount: null,
          expense_amount: d.amount,
        })
      })
      return _.orderBy(deps, 'date')
    },
    basicsData () {
      let data = []
      if (this.rec) {
        data.push({ text: 'Division', value: this.rec.account_obj.division ? this.rec.account_obj.division_obj.division : 'No Division' });
        data.push({ text: 'Account', value: this.rec.account_obj.name });
        data.push({ text: 'From date', value: this.$options.filters.shortDate(this.rec.from_date) });
        data.push({ text: 'To date', value: this.$options.filters.shortDate(this.rec.to_date) });
        data.push({ text: 'End balance', value: this.$options.filters.decMoney(this.rec.end_balance) });
      }
      return data
    },
    clearedDeposits () {
      return this.deposits.filter(d => d.reconciliation !== null)
    },
    clearedDepositsTotal () {
      return _.sumBy(this.clearedDeposits, 'amount')
    },
    clearedExpenses () {
      return this.expenses.filter(d => d.reconciliation !== null)
    },
    clearedExpensesTotal () {
      return _.sumBy(this.clearedExpenses, 'total')
    },
    clearedTotal () {
      return this.clearedDepositsTotal - this.clearedExpensesTotal
    },
    diff () {
      if (this.rec) {
        return this.clearedTotal - this.rec.end_balance
      }
      return ''
    },
    statusData () {
      let data = []
      if (this.rec) {
        data.push({ text: 'All Cleared Deposits', value: this.$options.filters.dec(this.clearedDepositsTotal) })
        data.push({ text: 'Subtract Cleared Expenses', value: this.$options.filters.decAccounting(this.clearedExpensesTotal) })
        data.push({ text: 'Equals Reconciled Balance', value: this.$options.filters.decAccounting(this.clearedTotal) })
        data.push({ text: 'Statement balance', value: this.$options.filters.dec(this.rec.end_balance) })
        data.push({ text: 'Difference', value: '' })
      }
      return data
    }
  },
  mounted () {
    this.getData()
  },
  methods: {
    editRec () {
      this.recCopy = _.cloneDeep(this.rec)
      this.showDialog = true
    },
    updateLine (line) {
      line.is_deposit ? miscServices.replaceObject(this.deposits, line.deposit) : miscServices.replaceObject(this.expenses, line.expense)
    },
    async updateRec () {
      this.isSaving = true;
      this.rec = await accountingAPI.updateReconciliation(this.recCopy);
      this.showDialog = false;
      this.isSaving = false;

      const tracker = {
          'tracker_data': {
            'description': 'Reconciliation details updated',
            'taken_by': this.$store.state.auth.profile.id,
            'action_date': moment().format('YYYY-MM-DD').toString(),
          },
          'reconciliation': this.rec.id
      }

      const res = await trackerAPI.createReconciliationTracker(tracker);
      this.$refs.actionTracker.pushItem(res);
    },
    async getData () {
      this.isLoading = true
      this.rec = await accountingAPI.getReconciliation(this.$route.params.id)
      this.deposits = await accountingAPI.getDepositsForReconciliation(this.$route.params.id)
      this.expenses = await accountingAPI.getExpensesForReconciliation(this.$route.params.id)
      // If the reconciliation is not locked, then get all uncleared lines as well
      if (!this.rec.is_locked) {
        this.deposits = this.deposits.concat(await accountingAPI.getUnclearedDeposits())
        this.expenses = this.expenses.concat(await accountingAPI.getUnclearedExpenses())
      }
      this.isLoading = false
    },
    async toggleLock () {
      this.rec.is_locked = !this.rec.is_locked;
      accountingAPI.updateReconciliation(this.rec);

      let description = '';
      description = this.rec.is_locked ? 'Reconciliation locked' : 'Reconciliation UN-locked';

      const tracker = {
          'tracker_data': {
            'description': description,
            'taken_by': this.$store.state.auth.profile.id,
            'action_date': moment().format('YYYY-MM-DD').toString(),
          },
          'reconciliation': this.rec.id
      }

      const res = await trackerAPI.createReconciliationTracker(tracker);
      this.$refs.actionTracker.pushItem(res);
      this.getData();
    }
  },
  components: {
    'rec-line': StatementLine,
    'reconciliation-tracker': ReconciliationTracker
  }
}
</script>
